import React, { useState, useEffect, useContext, useMemo } from "react";
import { 
  Box, Typography, Button, Grid, Paper, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Checkbox,
  IconButton, TextField, Snackbar, Alert
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from "react-router-dom";
import Navbar from "./layout/Navbar";
import Footer from "./layout/Footer";
import HomepageContext from "./context/HomepageContext";

const Cart = ({ isPopup = false }) => {
  const { handleCheckout, selected, setSelected, successMessage, handleSnackbarClose } = useContext(HomepageContext);
  const navigate = useNavigate();
  const [cart, setCart] = useState(() => {
    try {
      const cartData = sessionStorage.getItem('cart');
      return cartData ? JSON.parse(cartData) : [];
    } catch (error) {
      console.error("Error parsing cart data:", error);
      return [];
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem('cart', JSON.stringify(cart));
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error("Error saving cart data:", error);
    }
  }, [cart]);

  const handleQuantityChange = (index, newQuantity) => {
    if (newQuantity < 1 || !cart[index]) return;
    
    // Find all instances of this product
    const productId = cart[index].ProdtID;
    const productInstances = cart.filter(item => item.ProdtID === productId);
    
    // Calculate total existing quantity
    const totalQuantity = productInstances.reduce((sum, item) => sum + item.quantity, 0);
    
    // Calculate quantity difference
    const quantityDiff = newQuantity - totalQuantity;
    
    // Update all instances proportionally
    const newCart = cart.map(item => {
      if (item.ProdtID === productId) {
        return {
          ...item,
          quantity: item.quantity + Math.floor(quantityDiff / productInstances.length),
          total: item.ProdRetailPrice * (item.quantity + Math.floor(quantityDiff / productInstances.length))
        };
      }
      return item;
    });
  
    setCart(newCart);
    sessionStorage.setItem('cart', JSON.stringify(newCart));
  };

  const handleDelete = (index) => {
    if (!cart[index]) return;
    
    // Get product ID of item to delete
    const productId = cart[index].ProdtID;
    
    // Remove ALL items with this product ID
    const newCart = cart.filter(item => item.ProdtID !== productId);
    
    // Update both local state and session storage
    setCart(newCart);
    sessionStorage.setItem('cart', JSON.stringify(newCart));
    
    // Force cart context update
    window.dispatchEvent(new Event('cartUpdated'));
    
    // Update selected items
    setSelected(selected.filter(i => cart[i].ProdtID !== productId));
  };

  const handleSelect = (index) => {
    if (!cart[index]) return;
    
    const selectedIndex = selected.indexOf(index);
    let newSelected = [...selected];
    
    if (selectedIndex === -1) {
      newSelected.push(index);
    } else {
      newSelected.splice(selectedIndex, 1);
    }
    setSelected(newSelected);
  };

  // Safe calculation functions with null checks
  const calculateTotal = useMemo(() => {
    return selected.reduce((sum, index) => {
      const item = cart[index];
      return item && item.ProdRetailPrice 
        ? sum + (item.ProdRetailPrice * (item.quantity || 1)) 
        : sum;
    }, 0);
  }, [selected, cart]);

  const calculateTotalPV = useMemo(() => {
    return selected.reduce((sum, index) => {
      const item = cart[index];
      return item && item.ProductPV 
        ? sum + (item.ProductPV * (item.quantity || 1)) 
        : sum;
    }, 0);
  }, [selected, cart]);

  const handleSelectAll = () => {
    if (selected.length === cart.length) {
      setSelected([]);
    } else {
      setSelected(cart.map((_, index) => index));
    }
  };

  return (
    <>
      {!isPopup && <Navbar />}
      <Box maxWidth="lg" mx="auto" px={2} py={4}>
        <Typography variant="h4" gutterBottom>Your Cart</Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {cart.length === 0 ? (
              <Typography variant="body1">Your cart is empty</Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={cart.length > 0 && selected.length === cart.length}
                          indeterminate={selected.length > 0 && selected.length < cart.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="center">Quantity</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  {/* // In your Cart component */}
<TableBody>
  {cart.map((item, index) => {
    // Find if this product appears earlier in the array
    const firstOccurrenceIndex = cart.findIndex(
      (i) => i.ProdtID === item.ProdtID
    );
    
    // Only render if this is the first occurrence of the product
    if (firstOccurrenceIndex === index) {
      // Calculate total quantity for this product
      const totalQuantity = cart
        .filter(i => i.ProdtID === item.ProdtID)
        .reduce((sum, i) => sum + i.quantity, 0);
      
      return (
        <TableRow key={`${item.ProdtID}-${index}`}>
          <TableCell padding="checkbox">
            <Checkbox
              checked={selected.includes(index)}
              onChange={() => handleSelect(index)}
            />
          </TableCell>
          <TableCell>
            <Box display="flex" alignItems="center">
              <img
                src={item.ProdImage}
                alt={item.ProdName}
                style={{ width: 80, height: 80, marginRight: 16 }}
              />
              <Typography variant="body1">{item.ProdName}</Typography>
            </Box>
          </TableCell>
          <TableCell align="right">₹{item.ProdRetailPrice}</TableCell>
          <TableCell align="center">
            <Button 
              onClick={() => handleQuantityChange(index, totalQuantity - 1)}
              disabled={totalQuantity === 1}
            >
              -
            </Button>
            <TextField
              value={totalQuantity}
              size="small"
              sx={{ width: 60, mx: 1 }}
              inputProps={{ style: { textAlign: 'center' } }}
            />
            <Button 
              onClick={() => handleQuantityChange(index, totalQuantity + 1)}
            >
              +
            </Button>
          </TableCell>
          <TableCell align="right">
            ₹{item.ProdRetailPrice * totalQuantity}
          </TableCell>
          <TableCell align="right">
            <IconButton onClick={() => handleDelete(index)}>
              <DeleteIcon />
            </IconButton>
          </TableCell>
        </TableRow>
      );
    }
    return null; // Skip duplicate products
  })}
</TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Order Summary</Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Subtotal:</Typography>
                <Typography>₹{calculateTotal}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Shipping:</Typography>
                <Typography>₹0</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>TotalPv:</Typography>
                <Typography>{calculateTotalPV}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={3}>
                <Typography>Total:</Typography>
                <Typography variant="h6">₹{calculateTotal}</Typography>
              </Box>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleCheckout}
                disabled={selected.length === 0}
              >
                Proceed to Checkout ({selected.length})
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      {!isPopup && <Footer />}

      <Snackbar open={successMessage} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: "100%" }}>
          Transaction inserted successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default Cart;